import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { EMPTY, fromEvent, Subscription, timer } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { SystemStatusApiService } from '@backend/api/system-status/system-status-api.service';
import { ApiUnavailableModalComponent } from '@core/health-check/api-unavailable.modal/api-unavailable.modal.component';
import { ConnectivityStatus, ConnectivityView } from '@core/health-check/connectivity.model';
import { environment } from '../../../environments/environment';

/** Задержки между пробами, мс. Последняя повторяется, пока связь не восстановится. */
const PROBE_BACKOFF_MS = [1000, 2000, 4000, 8000, 10_000];

/** Сколько проб подряд должно упасть, прежде чем объявим обрыв. Защита от одиночного сбоя. */
const FAILURES_BEFORE_OFFLINE = 2;

/** Редкий фоновый опрос, когда всё хорошо. Ловит падение сервера при бездействии пользователя. */
const HEARTBEAT_MS = 30_000;

/**
 * Единственный источник правды о доступности основного API.
 *
 * Детекция реактивная: пока связь есть — только редкий heartbeat на видимой вкладке.
 * Подозрение включается сразу, как только любой прикладной запрос упал с сетевой ошибкой
 * (см. ConnectivityInterceptor) или браузер сообщил об уходе в offline. Дальше идут пробы
 * с нарастающей задержкой, и лишь после FAILURES_BEFORE_OFFLINE неудач подряд поднимается
 * блокирующая модалка. Возврат в online — по первой успешной пробе либо по первому успешному
 * ответу любого прикладного запроса.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService implements ConnectivityView {
  private systemStatusApi = inject(SystemStatusApiService);
  private nzModalService = inject(NzModalService);

  private readonly status = signal<ConnectivityStatus>('online');
  private readonly consecutiveFailures = signal(0);
  private readonly countdown = signal(0);

  readonly status$ = this.status.asReadonly();
  readonly isOffline$ = computed(() => this.status() === 'offline');
  /** Номер текущей попытки восстановления — показывается в модалке. */
  readonly attempt$ = this.consecutiveFailures.asReadonly();
  /** Секунд до следующей пробы. */
  readonly nextProbeInSec$ = this.countdown.asReadonly();

  private probeSub: Subscription | null = null;
  private heartbeatSub: Subscription | null = null;
  private modalRef: NzModalRef | null = null;

  constructor() {
    if (!environment.settings.enableHealthCheck) {
      return;
    }

    // Браузер точно знает, что сети нет — пробы бессмысленны, объявляем обрыв сразу.
    fromEvent(window, 'offline').subscribe(() => this.goOffline());
    fromEvent(window, 'online').subscribe(() => this.probeNow());

    effect(() => {
      const offline = this.isOffline$();
      untracked(() => (offline ? this.openModal() : this.closeModal()));
    });

    this.startHeartbeat();
  }

  /** Вызывается интерцептором при сетевой ошибке любого прикладного запроса. */
  reportNetworkFailure(): void {
    if (!environment.settings.enableHealthCheck || this.status() !== 'online') {
      return;
    }

    this.status.set('suspect');
    this.stopHeartbeat();
    this.scheduleProbe(PROBE_BACKOFF_MS[0]);
  }

  /** Вызывается интерцептором при любом успешном ответе — самый дешёвый признак живой связи. */
  reportSuccess(): void {
    if (this.status() !== 'online') {
      this.goOnline();
    }
  }

  /** Ручной retry по кнопке в модалке: сбрасывает backoff и бьёт пробой немедленно. */
  probeNow(): void {
    this.probeSub?.unsubscribe();
    this.probeSub = null;
    this.countdown.set(0);
    this.probe();
  }

  private probe(): void {
    this.systemStatusApi.healthCheck().subscribe({
      next: () => this.goOnline(),
      error: () => this.onProbeFailed(),
    });
  }

  private onProbeFailed(): void {
    this.stopHeartbeat();

    const failures = this.consecutiveFailures() + 1;
    this.consecutiveFailures.set(failures);
    this.status.set(failures >= FAILURES_BEFORE_OFFLINE ? 'offline' : 'suspect');

    this.scheduleProbe(PROBE_BACKOFF_MS[Math.min(failures, PROBE_BACKOFF_MS.length - 1)]);
  }

  private goOnline(): void {
    this.probeSub?.unsubscribe();
    this.probeSub = null;
    this.countdown.set(0);
    this.consecutiveFailures.set(0);
    this.status.set('online');
    this.startHeartbeat();
  }

  private goOffline(): void {
    if (!environment.settings.enableHealthCheck) {
      return;
    }

    this.stopHeartbeat();
    this.consecutiveFailures.update((o) => Math.max(o, FAILURES_BEFORE_OFFLINE));
    this.status.set('offline');
    this.scheduleProbe(PROBE_BACKOFF_MS[PROBE_BACKOFF_MS.length - 1]);
  }

  /** Один таймер и на задержку до пробы, и на обратный отсчёт для модалки. */
  private scheduleProbe(delayMs: number): void {
    this.probeSub?.unsubscribe();

    const ticks = Math.max(1, Math.ceil(delayMs / 1000));
    this.countdown.set(ticks);

    this.probeSub = timer(0, 1000)
      .pipe(take(ticks + 1))
      .subscribe((i) => {
        const left = ticks - i;
        this.countdown.set(Math.max(0, left));

        if (left <= 0) {
          this.probe();
        }
      });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatSub = fromEvent(document, 'visibilitychange')
      .pipe(
        startWith(null),
        map(() => document.visibilityState === 'visible'),
        // На скрытой вкладке не шумим: браузер всё равно душит таймеры, а трафик копится.
        switchMap((visible) => (visible ? timer(HEARTBEAT_MS, HEARTBEAT_MS) : EMPTY)),
      )
      .subscribe(() => this.probe());
  }

  private stopHeartbeat(): void {
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = null;
  }

  private openModal(): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.nzModalService.create<ApiUnavailableModalComponent, ConnectivityView>({
      nzContent: ApiUnavailableModalComponent,
      nzData: this,
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzFooter: null,
    });

    // Ссылку обнуляем всегда, иначе после первого восстановления связи
    // модалка больше никогда не откроется.
    this.modalRef.afterClose.subscribe(() => (this.modalRef = null));
  }

  private closeModal(): void {
    this.modalRef?.close();
    this.modalRef = null;
  }
}
